import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Alert, Box, Divider, Fab, List, ListItem, Popover, Typography } from '@mui/material'
import { IColumnSearchHistoryItem } from '@cbgms/components/grid/Hooks/use-list-search-history'
import { IColumn } from '@cbgms/components/grid/Types'
import { KnockOperator } from '@cbgms/components/grid/Knock/KnockTypes'
import HistoryIcon from '@mui/icons-material/History'

interface SearchHistoryProps {
  searchHistory: IColumnSearchHistoryItem[]
  columnNameTranslator: (key: string) => string
  onItemClick: (columns: IColumn[]) => void
  columns: IColumn[]
}

interface historyRecordProps {
  columnNameTranslator: (key: string) => string
  record: IColumnSearchHistoryItem
  onItemClick: (input: IColumnSearchHistoryItem) => void
}
const HistoryRecord = ({ record, columnNameTranslator, onItemClick }: historyRecordProps) => {
  const filterItems = record.filters.filter(f => !!f.FilterValue)

  return (
    <ListItem button onClick={() => onItemClick(record)}>
      {filterItems.map((f, i) => (
        <React.Fragment key={i}>
          <div key={i}>
            {columnNameTranslator(f.Name)}: <span style={{ fontWeight: 'bold' }}>{f.FilterValue}</span>
          </div>
          {i < filterItems.length - 1 && <Divider orientation='vertical' variant='middle' flexItem />}
        </React.Fragment>
      ))}
    </ListItem>
  )
}

const SearchHistoryButton: React.FC<SearchHistoryProps> = ({ searchHistory, columnNameTranslator, onItemClick, columns }) => {
  const { t } = useTranslation()
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null)

  const handlePopoverOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handlePopoverClose = () => {
    setAnchorEl(null)
  }

  const open = Boolean(anchorEl)
  const id = open ? 'search-history-popover' : undefined

  const onRecordClick = useCallback(
    (input: IColumnSearchHistoryItem) => {
      const newColumns = columns.map(a => {
        const newFilterValue = input.filters.find(f => f.Name === a.key) || {
          Name: a.key,
          FilterOperator: a.filterOperator,
          FilterValue: '' // this resets existing filter entries
        }
        return {
          ...a,
          key: newFilterValue.Name,
          filterOperator: newFilterValue.FilterOperator as any as KnockOperator,
          filter: newFilterValue.FilterValue
        }
      })
      onItemClick(newColumns)
    },
    [columns, onItemClick]
  )
  return (
    <>
      <Fab
        color='primary'
        size='small'
        data-testid='list-search-history-button'
        onClick={handlePopoverOpen}
        title={t('global.list.search_history')}
      >
        <HistoryIcon />
      </Fab>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography>{t('global.list.search_history')}</Typography>
          {!searchHistory?.length && <Alert severity='info'>{t('global.list.search_history_empty')}</Alert>}
          <List>
            {searchHistory.map((a, i) => (
              <HistoryRecord key={i} record={a} columnNameTranslator={columnNameTranslator} onItemClick={onRecordClick} />
            ))}
          </List>
        </Box>
      </Popover>
    </>
  )
}

export default SearchHistoryButton
